/**
 * Annual Forecast (Varshaphal) — 12-month outlook.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type { ChartCreateRequest, MonthlyForecast, VarshaphalResponse } from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

export function VarshaphalPage() {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const currentYear = new Date().getFullYear();
  const [forecastYear, setForecastYear] = useState<number>(currentYear);

  const reportMut = useMutation<VarshaphalResponse, Error, ChartCreateRequest>({
    mutationFn: (req) => api.varshaphal(req, forecastYear),
  });

  const onFormSubmit = useCallback(
    (req: ChartCreateRequest) => {
      reportMut.mutate(req);
    },
    [reportMut],
  );

  const submitError = useMemo(() => {
    if (!reportMut.error) return null;
    const e = reportMut.error;
    if (e instanceof ApiError) {
      if (e.status === 422) return t('अमान्य इनपुट', 'Invalid input');
    }
    return e.message ?? 'Error';
  }, [reportMut.error, lang]);

  const report = reportMut.data;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <header className="rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <h1 className="text-lg font-semibold text-saffron-100">
          🗓️ {t('वार्षिक भविष्यवाणी', 'Annual Forecast')}
        </h1>
        <p className="mt-1 text-xs text-stone-400">
          {t(
            'सोलर-रिटर्न से प्रारम्भ — 12 माह का दशा-आधारित आउटलुक।',
            '12-month dasha-based outlook starting from your solar return.',
          )}
        </p>
        <div className="mt-3 flex items-center gap-2 text-sm">
          <label className="text-stone-400">{t('वर्ष:', 'Year:')}</label>
          <input
            type="number"
            value={forecastYear}
            onChange={(e) => setForecastYear(Number(e.target.value) || currentYear)}
            className="w-24 rounded-md border border-stone-700 bg-stone-950 px-2 py-1 text-stone-100"
          />
        </div>
      </header>

      <ChartInputForm
        onSubmit={onFormSubmit}
        isSubmitting={reportMut.isPending}
        submitError={submitError}
      />

      {reportMut.isPending && (
        <p className="rounded-2xl bg-stone-900/40 p-8 text-center text-stone-400 ring-1 ring-stone-800">
          {t('गणना हो रही है…', 'Computing…')}
        </p>
      )}

      {report && <VarshaphalResult report={report} lang={lang} t={t} />}
    </div>
  );
}

const _TONE_STYLE: Record<MonthlyForecast['tone'], string> = {
  favourable: 'bg-emerald-900/30 text-emerald-200 ring-emerald-800/50',
  mixed: 'bg-amber-900/20 text-amber-200 ring-amber-800/50',
  challenging: 'bg-rose-950/30 text-rose-200 ring-rose-900/50',
};

function VarshaphalResult({
  report,
  lang,
  t,
}: {
  report: VarshaphalResponse;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  const takeaways = lang === 'hi' ? report.takeaways_hi : report.takeaways_en;
  return (
    <article className="space-y-5">
      <section className="rounded-2xl bg-gradient-to-br from-saffron-900/40 to-stone-900 p-5 ring-1 ring-saffron-700/40">
        <p className="text-xs uppercase tracking-wider text-saffron-300">
          {t('वर्ष-अधिपति एवं मुंथा', 'Year-lord and Muntha')}
        </p>
        <p className="mt-2 text-lg font-semibold text-saffron-100">
          {lang === 'hi' ? report.muntha_label_hi : report.muntha_label_en}
        </p>
        <p className="mt-1 text-sm text-stone-200">
          {lang === 'hi' ? report.muntha_theme_hi : report.muntha_theme_en}
        </p>
        <p className="mt-3 text-base leading-relaxed text-stone-100">
          {lang === 'hi' ? report.overall_summary_hi : report.overall_summary_en}
        </p>
      </section>

      {takeaways && takeaways.length > 0 && (
        <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-saffron-700/40">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-saffron-300">
            {t('मुख्य निष्कर्ष', 'Key Takeaways')}
          </h3>
          <ul className="ml-5 list-disc space-y-2 text-sm text-stone-100">
            {takeaways.map((tk, i) => (
              <li key={i}>{tk}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-saffron-200">
          {t('मासिक आउटलुक', 'Monthly Outlook')}
        </h3>
        {report.months.map((m) => (
          <div
            key={m.month}
            className={`rounded-md p-4 ring-1 ${_TONE_STYLE[m.tone]}`}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-mono text-stone-300">
                {m.start_date} → {m.end_date}
              </p>
              <span className="text-[10px] uppercase tracking-wider opacity-80">
                {m.tone === 'favourable'
                  ? t('अनुकूल', 'favourable')
                  : m.tone === 'challenging'
                    ? t('चुनौतीपूर्ण', 'challenging')
                    : t('मिश्र', 'mixed')}
              </span>
            </div>
            <p className="mt-1 text-base font-medium">
              {lang === 'hi' ? m.headline_hi : m.headline_en}
            </p>
            <p className="mt-1 text-sm opacity-90">
              {lang === 'hi' ? m.detail_hi : m.detail_en}
            </p>
            <p className="mt-1 text-[11px] opacity-70">
              {t('दशा', 'Dasha')}: {m.md_lord} – {m.ad_lord}
            </p>
          </div>
        ))}
      </section>

      {report.inner_practice && (
        <section className="rounded-2xl bg-emerald-950/30 p-5 ring-1 ring-emerald-800/40">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-emerald-200">
            {t('साधना', 'Inner Practice')}
          </h2>
          <p className="text-sm text-stone-200">
            {lang === 'hi' ? report.inner_practice.ishta_deva.text_hi
                           : report.inner_practice.ishta_deva.text_en}
          </p>
          {report.inner_practice.affirmations.length > 0 && (
            <ul className="ml-5 mt-3 list-disc space-y-1 text-sm italic text-emerald-200">
              {report.inner_practice.affirmations.map((af, i) => (
                <li key={i}>"{lang === 'hi' ? af.affirmation_hi : af.affirmation_en}"</li>
              ))}
            </ul>
          )}
          <p className="mt-3 rounded-md bg-stone-900/40 p-2 text-xs italic text-stone-300">
            {lang === 'hi' ? report.inner_practice.practice_instruction_hi
                           : report.inner_practice.practice_instruction_en}
          </p>
        </section>
      )}

      {report.methods && report.methods.length > 0 && (
        <section className="rounded-2xl bg-stone-900/40 p-4 text-xs text-stone-500 ring-1 ring-stone-800">
          <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-stone-500">
            {t('विधि एवं स्रोत', 'Methodology & Sources')}
          </h3>
          <ul className="space-y-1 leading-relaxed">
            {report.methods.map((m, i) => (
              <li key={i}>
                <span>{lang === 'hi' ? m.method_hi : m.method_en}</span>
                <span className="italic text-stone-600">
                  {' '}— {lang === 'hi' ? m.source_hi : m.source_en}
                </span>
              </li>
            ))}
          </ul>
          {(report.disclaimer_hi || report.disclaimer_en) && (
            <p className="mt-3 italic text-stone-600">
              {lang === 'hi' ? report.disclaimer_hi : report.disclaimer_en}
            </p>
          )}
        </section>
      )}

      <p className="text-center text-[10px] text-stone-600">{report.rules_version}</p>
    </article>
  );
}
